import PropTypes from 'prop-types';
import React from 'react';
import { ButtonGroup, Button } from 'react-bootstrap';
import { cloneDeep, trimStart } from 'lodash';
import * as Table from 'reactabular-table';
import * as edit from 'react-edit';
import { each, map } from 'lodash/collection';
import { withTranslation, Translation } from 'react-i18next';
import {
  DEFAULT_DISTANCE_UNIT,
  DEFAULT_WEIGHT_UNIT,
} from 'utils/localization/types';
import TwoElementsCell from './TwoElementsCell';
import ZoneHeader from './ZoneHeader';
import RangeInput from './RangeInput';
import PriceInput from './PriceInput';
import Validate from './Validate';

const epsilon = 0.00001;

const isFloatLessThan = (a, b) =>
  b - a > (Math.abs(a) < Math.abs(b) ? Math.abs(b) : Math.abs(a)) * epsilon;

const pricesMatrixToCsv = (tariffName, data, t) => {
  let csvContent = 'data:text/csv;charset=utf-8,';
  let dataString = `${t('LOAD_RANGE')},${t('WEIGHT_RANGE')},`;
  each(data.zonesList, (zone, index) => {
    dataString += `${t('ZONE_NUMBER_DISTANCE', {
      zoneIndex: index + 1,
      distance: `${zone.minDistance}-${zone.maxDistance}`,
    })},`;
  });
  dataString += '\r\n';
  csvContent += dataString;
  each(data.rows, (row) => {
    dataString = `${row.loadRange.minPallets}-${row.loadRange.maxPallets},`;
    dataString += `${row.weightRange.minWeight}-${row.weightRange.maxWeight},`;
    each(row.pricesList, (price) => {
      dataString += `${price},`;
    });
    csvContent += `${dataString}\r\n`;
  });
  const filename = `${tariffName}.csv`;
  const encodedUri = encodeURI(csvContent);
  const link = (
    <div>
      <Translation>
        {(t) => (
          <a
            href={encodedUri}
            download={filename}
            target="_blank"
            rel="noreferrer"
          >
            {t('EXPORT_TO_CSV')}
          </a>
        )}
      </Translation>
    </div>
  );
  return link;
};

const dataWithTariff = (tariff) => {
  const data = {};
  data.zonesList = map(tariff.zones, function (zone, index) {
    return {
      minDistance: zone[0],
      maxDistance: zone[1],
    };
  });

  data.rows = [];
  const count = tariff.load_ranges ? tariff.load_ranges.length : 0;
  for (let i = 0; i < count; ++i) {
    const pricesList = map(tariff.zone_to_load, function (item) {
      if (i < item.length) {
        return item[i];
      }
    });
    let minWeight = 0;
    let maxWeight = 0;
    if (i < tariff.weight_ranges.length) {
      minWeight = tariff.weight_ranges[i][0];
      maxWeight = tariff.weight_ranges[i][1];
    }
    const row = {
      loadRange: {
        minPallets: tariff.load_ranges[i][0],
        maxPallets: tariff.load_ranges[i][1],
      },
      weightRange: {
        minWeight: minWeight,
        maxWeight: maxWeight,
      },
      pricesList: pricesList,
    };
    data.rows.push(row);
  }
  return data;
};

const loadRangesFromData = (data) =>
  map(data.rows, (row) => {
    const loadRange = row.loadRange;
    return [loadRange.minPallets, loadRange.maxPallets];
  });

const weightRangesFromData = (data) =>
  map(data.rows, (row) => {
    const weightRange = row.weightRange;
    return [weightRange.minWeight, weightRange.maxWeight];
  });

const zonesFromData = (data) =>
  map(data.zonesList, (zone) => [zone.minDistance, zone.maxDistance]);

const zoneToLoadFromData = (data) => {
  const zone_to_load = [];
  each(data.rows, (row, rowIndex) => {
    each(row.pricesList, (price, priceIndex) => {
      if (!zone_to_load[priceIndex]) {
        zone_to_load[priceIndex] = [];
      }
      zone_to_load[priceIndex][rowIndex] = price;
    });
  });
  return zone_to_load;
};

class TariffPricesTable extends React.Component {
  static propTypes = {
    tariff: PropTypes.object,
    onChange: PropTypes.func,
  };

  constructor(props) {
    super(props);
    const formatters = {
      fixedFloat: (value) => parseFloat(value).toFixed(2),
      fixedInt: (value) => parseInt(value, 10),
    };
    const validators = {
      prices: (value, { property, rowIndex }) => {
        return Validate(
          (value) => value > 0,
          (value) => {
            const row = this.state.data.rows[rowIndex];
            const priceIndex = parseInt(trimStart(property, 'Price'), 10) - 1;
            if (priceIndex) {
              const prevRowPrice = row.pricesList[priceIndex - 1];
              if (isFloatLessThan(value, prevRowPrice)) {
                return true;
              }
            }
            if (rowIndex) {
              const prevRow = this.state.data.rows[rowIndex - 1];
              const prevColPrice = prevRow.pricesList[priceIndex];
              if (isFloatLessThan(value, prevColPrice)) {
                return true;
              }
            }
            return false;
          },
        )(value);
      },
      zones: (zone, index) =>
        Validate((value) => {
          const minDistance = zone.minDistance;
          const maxDistance = zone.maxDistance;
          if (value === maxDistance) {
            return value > 0 && value >= minDistance;
          }
          return value > 0 || !index;
        }),
    };

    this.state = {
      tariff: {},
      formatters: formatters,
      data: { rows: [] },
      editedCell: null,
      editedKey: null,
      validators: validators,
    };
  }

  componentDidMount() {
    const tariff = this.props.tariff;
    this.setState((prevState) => {
      let data = prevState.data;
      if (tariff) {
        data = dataWithTariff(tariff);
        return { data };
      }
      return {};
    });
  }

  createRow = (index) => {
    const dataRows = this.state.data.rows || [];
    let newRow = {};
    if (dataRows.length && index > 0 && index <= dataRows.length) {
      const lastRow = dataRows[index - 1];
      newRow = JSON.parse(JSON.stringify(lastRow));
    } else {
      newRow = {
        loadRange: { minPallets: 0, maxPallets: 0 },
        weightRange: { minWeight: 0, maxWeight: 0 },
      };
    }
    return newRow;
  };

  addRow = () => {
    let data;
    this.setState((prevState) => {
      data = prevState.data;
      const dataRows = data.rows || [];
      const index = dataRows.length;
      const newRow = this.createRow(index);
      dataRows.push(newRow);
      data.rows = dataRows;
      return { data: data };
    });
    if (data) {
      this.onChangePrices({
        load_ranges: loadRangesFromData(data),
        weight_ranges: weightRangesFromData(data),
        zone_to_load: zoneToLoadFromData(data),
      });
    }
  };

  insertRow = (index) => {
    let data;
    this.setState((prevState) => {
      data = prevState.data;
      const dataRows = data.rows || [];
      const newRow = this.createRow(index);
      if (dataRows.length && index <= dataRows.length) {
        dataRows.splice(index, 0, newRow);
        return { data };
      }
      return {};
    });
    if (data) {
      this.onChangePrices({
        load_ranges: loadRangesFromData(data),
        weight_ranges: weightRangesFromData(data),
        zone_to_load: zoneToLoadFromData(data),
      });
    }
  };

  removeRow = (index) => {
    let data;
    this.setState((prevState) => {
      data = prevState.data;
      const dataRows = data.rows || [];
      if (dataRows.length && index < dataRows.length) {
        dataRows.splice(index, 1);
        data.rows = dataRows;
        return { data };
      }
      return {};
    });
    if (data) {
      this.onChangePrices({
        load_ranges: loadRangesFromData(data),
        weight_ranges: weightRangesFromData(data),
        zone_to_load: zoneToLoadFromData(data),
      });
    }
  };

  addZone = () => {
    let data;
    this.setState((prevState) => {
      data = prevState.data;
      if (!data.zonesList) {
        data.zonesList = [];
      }
      data.zonesList.push({
        minDistance: 0,
        maxDistance: 0,
      });
      each(data.rows, function (item) {
        if (!item.pricesList) {
          item.pricesList = [];
        }
        item.pricesList.push(0);
      });
      return { data };
    });
    if (data) {
      this.onChangePrices({
        zones: zonesFromData(data),
        zone_to_load: zoneToLoadFromData(data),
      });
    }
  };

  removeZone = (index) => {
    let data;
    this.setState((prevState) => {
      data = prevState.data;
      data.zonesList.splice(index, 1);
      each(data.rows, function (item) {
        if (item.pricesList) {
          item.pricesList.splice(index, 1);
        }
      });
      return { data };
    });
    if (data) {
      this.onChangePrices({
        zones: zonesFromData(data),
        zone_to_load: zoneToLoadFromData(data),
      });
    }
  };

  onChangePrices = (changedProperty) => {
    if (this.props.onChange) {
      this.props.onChange(changedProperty);
    }
  };

  tableData = () => {
    const data = this.state.data;
    const tableData = map(data.rows, function (item) {
      const row = {
        loadRange: item.loadRange,
        weightRange: item.weightRange,
        editing: item.editing,
      };
      each(item.pricesList, function (priceItem, index) {
        const priceKey = `Price${index + 1}`;
        row[priceKey] = priceItem;
      });
      return row;
    });
    return tableData;
  };

  tableColumns = () => {
    const { t } = this.props;
    const formatters = this.state ? this.state.formatters : {};
    const data = this.state.data;
    const validators = this.state.validators;
    const editableRange = edit.edit({
      isEditing: ({ columnIndex, rowData }) => columnIndex === rowData.editing,
      onActivate: ({ columnIndex, rowIndex }) => {},
      onValue: ({ value, rowIndex, property }) => {
        let dataClone;
        this.setState(
          (prevState) => {
            dataClone = cloneDeep(prevState.data);
            const row = dataClone.rows[rowIndex];
            row[property] = value;
            row.editing = false;
            dataClone.rows[rowIndex] = row;
            return { data: dataClone, editedKey: null };
          },
          () => {
            if (dataClone) {
              if (property === 'loadRange') {
                this.onChangePrices({
                  load_ranges: loadRangesFromData(dataClone),
                });
              } else if (property === 'weightRange') {
                this.onChangePrices({
                  weight_ranges: weightRangesFromData(dataClone),
                });
              }
            }
          },
        );
      },
    });
    const weightRangeCell = TwoElementsCell.bind(
      this,
      'minWeight',
      'maxWeight',
    );
    const loadRangeCell = TwoElementsCell.bind(
      this,
      'minPallets',
      'maxPallets',
    );
    const columns = [
      {
        property: 'loadRange',
        header: {
          label: t('LOAD_RANGE_PALLETS'),
        },
        cell: {
          formatters: [loadRangeCell()],
          transforms: [
            editableRange(RangeInput.bind(this, 'minPallets', 'maxPallets')()),
          ],
        },
      },
      {
        property: 'weightRange',
        header: {
          label: t('WEIGHT_RANGE_WITH_UNIT', {
            weight_units: DEFAULT_WEIGHT_UNIT,
          }),
        },
        cell: {
          formatters: [weightRangeCell()],
          transforms: [
            editableRange(RangeInput.bind(this, 'minWeight', 'maxWeight')()),
          ],
        },
      },
    ];

    if (data.rows) {
      each(data.zonesList, (zone, index) => {
        let property = `Zone ${index + 1}, ${DEFAULT_DISTANCE_UNIT}`;
        let headerEditor = ZoneHeader.bind(
          this,
          zone,
          'editedCell',
          property,
          (value, key) => {
            zone[key] = parseInt(value, 10);
            this.setState({ editedKey: null });
            this.onChangePrices({ zones: zonesFromData(data) });
          },
        );
        const columnProperty = `Price${index + 1}`;
        headerEditor = headerEditor({
          validator: validators.zones(zone, index),
        });
        const removeZone = () => {
          this.removeZone(index);
        };
        property = this.props.t('ZONE_NUMBER_WITH_DISTANCE_UNIT', {
          number: index + 1,
          distanceUnit: DEFAULT_DISTANCE_UNIT,
        });

        const columnHeader = (
          <div>
            <div>
              {`${property} `}
              <Button
                bsSize="xsmall"
                className="btn btn-danger"
                onClick={removeZone}
              >
                &#10006;
              </Button>
            </div>
            <div {...headerEditor.props}>{headerEditor.value}</div>
          </div>
        );
        const editable = edit.edit({
          isEditing: ({ columnIndex, rowData }) =>
            columnIndex === rowData.editing,
          onActivate: ({ columnIndex, rowIndex }) => {
            this.setState((prevState) => {
              const dataClone = cloneDeep(prevState.data);
              dataClone.rows[rowIndex].editing = columnIndex;
              return { data: dataClone };
            });
          },
          onValue: ({ value, rowIndex, property }) => {
            let dataClone;
            this.setState(
              (prevState) => {
                dataClone = cloneDeep(prevState.data);
                const row = dataClone.rows[rowIndex];
                const priceIndex =
                  parseInt(trimStart(property, 'Price'), 10) - 1;
                const updatedValue = parseFloat(value);
                row.pricesList[priceIndex] = updatedValue;
                row.editing = false;
                return { data: dataClone, editedKey: null };
              },
              () => {
                if (dataClone) {
                  this.onChangePrices({
                    zone_to_load: zoneToLoadFromData(dataClone),
                  });
                }
              },
            );
          },
        });

        columns.push({
          property: columnProperty,
          header: {
            label: columnHeader,
          },
          cell: {
            formatters: [formatters.fixedFloat, validators.prices],
            transforms: [editable(PriceInput())],
          },
          zoneColumn: true,
        });
      });

      columns.push({
        header: {
          formatters: [
            () => (
              <Button
                bsSize="xsmall"
                className="btn btn-default"
                onClick={this.addZone}
                disabled={data.rows.length <= 0}
              >
                {t('ADD_ZONE')}
              </Button>
            ),
          ],
        },
        cell: {
          formatters: [
            (value, { rowData, rowIndex }) => {
              const remove = () => {
                this.removeRow(rowIndex);
              };
              const insert = () => {
                const nextIndex = rowIndex + 1;
                this.insertRow(nextIndex);
              };

              return (
                <div>
                  <ButtonGroup bsSize="xsmall">
                    <Button className="btn btn-success" onClick={insert}>
                      &#10010;
                    </Button>
                    <Button className="btn btn-danger" onClick={remove}>
                      &#10006;
                    </Button>
                  </ButtonGroup>
                </div>
              );
            },
          ],
        },
      });
    }
    return columns;
  };

  render() {
    const columns = this.tableColumns();
    const tableData = this.tableData();
    const { t } = this.props;
    return (
      <Table.Provider className="default-table" columns={columns}>
        <Table.Header />
        <Table.Body
          rows={tableData}
          rowKey={({ rowIndex }) => `${rowIndex}price-table`}
        />
        <tfoot>
          <tr>
            {columns.map((column, i) => {
              if (i === 0) {
                return (
                  <td key={`${i}-footer`}>
                    <Button
                      bsSize="xsmall"
                      className="btn btn-default"
                      onClick={this.addRow}
                    >
                      {t('ADD_RANGE_ROW')}
                    </Button>
                  </td>
                );
              }
              if (i === columns.length - 1) {
                const tariff = this.props.tariff;
                const link = pricesMatrixToCsv(tariff.name, this.state.data, t);
                return <td key={`${i}-footer`}>{link}</td>;
              }
              return <td key={`${i}-footer`} />;
            })}
          </tr>
        </tfoot>
      </Table.Provider>
    );
  }
}

export default withTranslation()(TariffPricesTable);
